<script lang="ts" setup>
import { computed } from "vue";
import BaseChart from "@/views/shared/components/BaseChart.vue";
import {
  useEngineSeries,
  type EngineSeriesRequest,
  type SeriesDescription,
} from "@/queries/engine-metrics";
import type { Ref } from "vue";
import type { Plugin } from "chart.js";

/**
 * One row of the correlation view: a single series on its own Y axis, over the window every row shares.
 *
 * Its own axis and not a shared one, deliberately. These series are in milliseconds, request units, bytes
 * and plain counts, so one axis across them would draw a latency and a charge at comparable heights and
 * mean nothing by it. Correlation is read down the column instead — the time axis is what the rows have
 * in common, which is the only thing they honestly share.
 */
const props = defineProps<{
  request: EngineSeriesRequest;
  /** What may be asked of this series, so the row's own pickers cannot offer an unanswerable pair. */
  description?: SeriesDescription;
  removable?: boolean;
  /**
   * The step every row is pointing at, or null when nothing is hovered. Held by the page rather than the
   * row so hovering one row marks the same instant on all of them — which is how a correlation read down
   * the column stays on one column.
   */
  hoverIndex?: number | null;
}>();

const emit = defineEmits<{
  (event: "update", request: EngineSeriesRequest): void;
  (event: "remove"): void;
  (event: "hover", index: number | null): void;
}>();

const request = computed(() => props.request) as Ref<EngineSeriesRequest>;
const { lines, unit, seriesLoading, seriesError } = useEngineSeries(request);

// Every line shares the window's steps, so the labels come from whichever arrived — an unsplit query has
// one line, a split has one per value, and all of them are stepped identically by the server.
const labels = computed(() =>
  (lines.value[0]?.points ?? []).map((point) =>
    new Date(point.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  )
);

// Brand palette (DESIGN-GUIDELINES §Colors). Cycles — a split with more values than colours repeats a
// colour rather than inventing one, and the legend still names each line.
const LINE_COLOURS = ["#33658a", "#ff9933", "#198754", "#f44336", "#669fcd", "#fd9d17", "#6c757d"];

const data = computed(() => ({
  labels: labels.value,
  datasets: lines.value.map((line, index) => ({
    label: line.label || props.request.series,
    data: line.points.map((point) => point.value),
    borderColor: LINE_COLOURS[index % LINE_COLOURS.length],
    backgroundColor: LINE_COLOURS[index % LINE_COLOURS.length],
    borderWidth: 2,
    fill: false,
  })),
}));

/**
 * The hairline every row draws at the shared step. Each chart draws its own because the rows' plot areas
 * do not start at the same x — a row whose axis reads "12000" is inset further than one reading "0.5" —
 * so one line over the stack would land on a different instant in each.
 *
 * <p>The index rides in <c>options</c> rather than being pushed at the chart instance: vue-chartjs already
 * redraws when options change, so there is nothing here to keep in step by hand.</p>
 */
const crosshair: Plugin<"line"> = {
  id: "linkedCrosshair",
  afterDatasetsDraw(chart) {
    const index = (chart.options as { linkedIndex?: number | null }).linkedIndex;
    if (index === null || index === undefined) return;

    const x = chart.scales.x.getPixelForValue(index);
    const { top, bottom } = chart.chartArea;

    chart.ctx.save();
    chart.ctx.strokeStyle = "rgba(108, 117, 125, 0.7)";
    chart.ctx.lineWidth = 1;
    chart.ctx.setLineDash([3, 3]);
    chart.ctx.beginPath();
    chart.ctx.moveTo(x, top);
    chart.ctx.lineTo(x, bottom);
    chart.ctx.stroke();
    chart.ctx.restore();
  },
};

const options = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    // One line and its own title above the chart already names it, so a legend would say it twice.
    legend: { display: lines.value.length > 1, position: "bottom" as const },
  },
  scales: {
    y: { beginAtZero: true, title: { display: true, text: unit.value } },
    x: { ticks: { maxTicksLimit: 8 } },
  },
  elements: { point: { radius: 0 } },
  interaction: { mode: "index" as const, intersect: false },
  linkedIndex: props.hoverIndex ?? null,
  onHover: (_event: unknown, active: { index: number }[]) =>
    emit("hover", active.length > 0 ? active[0].index : null),
}));

const hasPoints = computed(() => lines.value.some((line) => line.points.length > 0));

const update = (patch: Partial<EngineSeriesRequest>) =>
  emit("update", { ...props.request, ...patch });
</script>

<template>
  <div class="border rounded p-3">
    <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
      <span class="fw-semibold">{{ request.series }}</span>

      <select
        class="form-select form-select-sm w-auto"
        :value="request.aggregation"
        @change="update({ aggregation: ($event.target as HTMLSelectElement).value as never })"
      >
        <option v-for="option in description?.aggregations ?? []" :key="option" :value="option">
          {{ option }}
        </option>
      </select>

      <select
        class="form-select form-select-sm w-auto"
        :value="request.splitBy ?? 'None'"
        @change="update({ splitBy: ($event.target as HTMLSelectElement).value as never })"
      >
        <option v-for="option in description?.splits ?? []" :key="option" :value="option">
          {{ option === "None" ? "No split" : option }}
        </option>
      </select>

      <span v-if="unit" class="badge bg-tertiary">{{ unit }}</span>
      <span v-if="seriesLoading" class="spinner-border spinner-border-sm text-muted"></span>

      <button
        v-if="removable"
        class="bb-btn-cancel btn-sm ms-auto"
        title="Remove series"
        @click="emit('remove')"
      >
        <i class="bi bi-x-lg"></i>
      </button>
    </div>

    <div v-if="seriesError" class="alert alert-warning mb-0 py-2 small">
      {{ seriesError.message }}
    </div>

    <!-- Nothing recorded is an ordinary answer and not a failure: a quiet engine has no points, and so
         does an org that ran nothing in the window. Said plainly, because an empty chart looks broken. -->
    <div v-else-if="!hasPoints && !seriesLoading" class="text-muted small py-4 text-center">
      Nothing recorded in this window.
    </div>

    <BaseChart
      v-else
      type="line"
      :data="data"
      :options="options"
      :plugins="[crosshair]"
      :height="160"
      @mouseleave="emit('hover', null)"
    />
  </div>
</template>
